import React, { useState, useEffect, useContext } from 'react';
import { X, ShoppingBag, Plus, Minus, Trash2 } from 'lucide-react';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth.jsx';
import cartService from '../services/cartService';

const CartOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 9998;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const CartSidebar = styled.div`
  position: fixed;
  top: 0;
  right: ${props => props.$isOpen ? '0' : '-500px'};
  width: 450px;
  height: 100vh;
  background: linear-gradient(135deg, #1a1a2e, #0f0f23);
  border-left: 2px solid rgba(255, 255, 255, 0.1);
  box-shadow: -10px 0 30px rgba(0, 0, 0, 0.5);
  transition: right 0.3s ease;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 640px) {
    width: 100%;
    right: ${props => props.$isOpen ? '0' : '-100%'};
  }
`;

const CartHeader = styled.div`
  padding: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: white;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    &::before {
      content: '🛒';
      font-size: 1.8rem;
    }
  }
  
  .close-btn {
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.6);
    font-size: 1.5rem;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      color: white;
      transform: rotate(90deg);
    }
  }
`;

const CartContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
`;

const CartItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 1rem;
  display: flex;
  gap: 1rem;
  
  .product-image {
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, #3b82f6, #9333ea);
    border-radius: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    flex-shrink: 0;
  }
  
  .product-details {
    flex: 1;
    
    h3 {
      font-size: 1.1rem;
      font-weight: 600;
      margin-bottom: 0.5rem;
      color: white;
    }
    
    .config {
      font-size: 0.85rem;
      color: rgba(255, 255, 255, 0.7);
      margin-bottom: 0.5rem;
    }
    
    .offer-badge {
      display: inline-block;
      background: rgba(239, 68, 68, 0.2);
      color: #ef4444;
      padding: 0.25rem 0.75rem;
      border-radius: 9999px;
      font-size: 0.75rem;
      font-weight: 600;
      margin-bottom: 0.5rem;
    }
  }
  
  .product-actions {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: space-between;
    
    .price {
      font-size: 1.2rem;
      font-weight: 700;
      color: #10b981;
      margin-bottom: 0.5rem;
    }
    
    .quantity-controls {
      margin-bottom: 0.5rem;
      
      .quantity-input {
        width: 60px;
        padding: 0.25rem;
        border: 1px solid rgba(255, 255, 255, 0.3);
        border-radius: 0.25rem;
        background: rgba(255, 255, 255, 0.1);
        color: white;
        text-align: center;
        font-weight: 600;
        font-size: 0.9rem;
        
        &:focus {
          outline: none;
          border-color: rgba(255, 255, 255, 0.6);
          background: rgba(255, 255, 255, 0.15);
        }
        
        &::placeholder {
          color: rgba(255, 255, 255, 0.5);
        }
      }
    }
    
    .remove-btn {
      background: transparent;
      border: none;
      color: #ef4444;
      font-size: 0.85rem;
      cursor: pointer;
      transition: all 0.3s ease;
      
      &:hover {
        color: #dc2626;
        text-decoration: underline;
      }
    }
  }
`;

const CartFooter = styled.div`
  padding: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  
  .total-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    
    .total-label {
      font-size: 1.2rem;
      font-weight: 600;
      color: white;
    }
    
    .total-price {
      font-size: 1.8rem;
      font-weight: 700;
      color: #10b981;
    }
  }
  
  .cart-actions {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    
    button {
      padding: 1rem;
      border-radius: 0.5rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
      border: none;
      font-size: 1rem;
    }
    
    .checkout-btn {
      background: linear-gradient(135deg, #10b981, #059669);
      color: white;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 30px rgba(16, 185, 129, 0.3);
      }
    }
    
    .continue-btn {
      background: transparent;
      color: rgba(255, 255, 255, 0.8);
      border: 1px solid rgba(255, 255, 255, 0.3);
      
      &:hover {
        background: rgba(255, 255, 255, 0.1);
      }
    }
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 3rem;
  color: rgba(255, 255, 255, 0.6);
  
  .empty-icon {
    font-size: 4rem;
    margin-bottom: 1rem;
  }
  
  h3 {
    font-size: 1.3rem;
    margin-bottom: 0.5rem;
    color: white;
  }
  
  p {
    margin-bottom: 2rem;
  }
  
  button {
    background: linear-gradient(135deg, #3b82f6, #9333ea);
    color: white;
    border: none;
    padding: 0.75rem 2rem;
    border-radius: 9999px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 30px rgba(59, 130, 246, 0.3);
    }
  }
`;

const FloatingCartButton = styled.button`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #9333ea);
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(59, 130, 246, 0.3);
  transition: all 0.3s ease;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 15px 40px rgba(59, 130, 246, 0.4);
  }
  
  .cart-count {
    position: absolute;
    top: -5px;
    right: -5px;
    background: #ef4444;
    color: white;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    font-size: 0.75rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid white;
  }
`;

// Contexte du panier
const CartContext = React.createContext();

export function useCart() {
  const context = React.useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const { isAuthenticated, user } = useAuth();
  const [wasAuthenticated, setWasAuthenticated] = useState(false);

  // Charger le panier depuis l'API ou localStorage au démarrage et lors des changements d'auth
  useEffect(() => {
    const loadCart = async () => {
      try {
        // Tenter de charger depuis l'API d'abord
        const cartItems = await cartService.getCart();
        setCartItems(cartItems);
        
        // Synchroniser localStorage
        localStorage.setItem('inza_cart', JSON.stringify(cartItems));
      } catch (error) {
        console.log('Error loading cart from API, using localStorage');
        // Fallback vers localStorage
        const savedCart = localStorage.getItem('inza_cart');
        if (savedCart) {
          setCartItems(JSON.parse(savedCart));
        }
      }
    };
    
    loadCart();
  }, [isAuthenticated, user]); // Recharger quand l'auth change

  // Vider le panier lors de la déconnexion (uniquement si l'utilisateur était connecté avant)
  useEffect(() => {
    // Ne vider le panier que si l'utilisateur se déconnecte (passage de connecté à non connecté)
    if (wasAuthenticated && !isAuthenticated && cartItems.length > 0) {
      console.log('Utilisateur déconnecté, vidage du panier');
      setCartItems([]);
      localStorage.removeItem('inza_cart');
      
      // Optionnel: vider aussi le panier côté serveur
      cartService.clearCart().catch(error => {
        console.log('Erreur lors du vidage du panier serveur:', error);
      });
    }
    
    // Mettre à jour l'état précédent
    setWasAuthenticated(isAuthenticated);
  }, [isAuthenticated]); // Seulement quand l'auth change

  // Sauvegarder le panier dans localStorage
  useEffect(() => {
    console.log('Saving cartItems to localStorage:', cartItems);
    localStorage.setItem('inza_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = async (product, configuration, offer = null) => {
    console.log('addToCart called with:', { product, configuration, offer });
    console.log('User authenticated:', isAuthenticated, 'User:', user);
    
    // Si l'utilisateur n'est pas connecté, utiliser directement le localStorage
    if (!isAuthenticated || !user) {
      console.log('User not authenticated, using localStorage only');
      
      const existingItem = cartItems.find(item => 
        item.id === product.id && 
        item.storage === configuration.storage && 
        item.color === configuration.color
      );

      if (existingItem) {
        console.log('Updating existing item in localStorage');
        setCartItems(cartItems.map(item =>
          item.id === product.id && 
          item.storage === configuration.storage && 
          item.color === configuration.color
            ? { ...item, quantity: item.quantity + 1 }
            : item
        ));
      } else {
        console.log('Adding new item to localStorage');
        const newItem = {
          ...product,
          storage: configuration.storage,
          color: configuration.color,
          quantity: 1,
          offer: offer,
          addedAt: new Date().toISOString()
        };
        console.log('New item to add:', newItem);
        console.log('Current cartItems before add:', cartItems);
        const newCart = [...cartItems, newItem];
        console.log('New cart after add:', newCart);
        setCartItems(newCart);
      }
      return;
    }
    
    // Si l'utilisateur est connecté, utiliser l'API
    try {
      const result = await cartService.addToCart(product, configuration, offer);
      console.log('cartService.addToCart result:', result);
      
      const updatedCart = await cartService.getCart();
      console.log('updatedCart from API:', updatedCart);
      setCartItems(updatedCart);
      
    } catch (error) {
      console.error('Error adding to cart via API, using localStorage:', error);
      
      const existingItem = cartItems.find(item => 
        item.id === product.id && 
        item.storage === configuration.storage && 
        item.color === configuration.color
      );

      if (existingItem) {
        console.log('Updating existing item (fallback)');
        setCartItems(cartItems.map(item =>
          item.id === product.id && 
          item.storage === configuration.storage && 
          item.color === configuration.color
            ? { ...item, quantity: item.quantity + 1 }
            : item
        ));
      } else {
        console.log('Adding new item (fallback)');
        const newItem = {
          ...product,
          storage: configuration.storage,
          color: configuration.color,
          quantity: 1,
          offer: offer,
          addedAt: new Date().toISOString()
        };
        setCartItems([...cartItems, newItem]);
      }
    }
  };

  const removeFromCart = (itemId, storage, color) => {
    setCartItems(cartItems.filter(item =>
      !(item.id === itemId && item.storage === storage && item.color === color)
    ));
  };

  const updateQuantity = (itemId, storage, color, quantity) => {
    if (quantity <= 0) {
      removeFromCart(itemId, storage, color);
    } else {
      setCartItems(cartItems.map(item =>
        item.id === itemId && item.storage === storage && item.color === color
          ? { ...item, quantity }
          : item
      ));
    }
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
      const price = item.total_price || item.price || 0;
      const quantity = item.quantity || 1;
      return total + (parseFloat(price) * quantity);
    }, 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  const refreshCart = async () => {
    try {
      const updatedCart = await cartService.getCart();
      setCartItems(updatedCart);
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      isCartOpen,
      setIsCartOpen,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getCartTotal,
      getCartCount,
      refreshCart
    }}>
      {children}
    </CartContext.Provider>
  );
}

function Cart() {
  const { 
    cartItems, 
    isCartOpen, 
    setIsCartOpen, 
    removeFromCart, 
    updateQuantity, 
    clearCart, 
    getCartTotal, 
    getCartCount 
  } = useCart();
  const { isAuthenticated, user } = useAuth();

  const handleCheckout = () => {
    // Vérifier si l'utilisateur est connecté
    if (!isAuthenticated || !user) {
      // Rediriger vers la page de connexion avec l'URL de retour
      const currentUrl = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?redirect=${currentUrl}`;
      return;
    }
    
    // Redirection vers la page de commande avec les articles du panier
    const params = new URLSearchParams();
    cartItems.forEach((item, index) => {
      params.append(`product_${index}`, item.id);
      params.append(`storage_${index}`, item.storage);
      params.append(`color_${index}`, item.color);
      params.append(`quantity_${index}`, item.quantity);
      if (item.offer) {
        params.append(`offer_${index}`, item.offer.id);
      }
    });
    
    window.location.href = `/checkout?${params.toString()}`;
  };

  return (
    <>
      <FloatingCartButton onClick={() => setIsCartOpen(true)}>
        🛒
        {getCartCount() > 0 && (
          <span className="cart-count">{getCartCount()}</span>
        )}
      </FloatingCartButton>

      <CartOverlay $isOpen={isCartOpen} onClick={() => setIsCartOpen(false)}>
        <CartSidebar $isOpen={isCartOpen} onClick={(e) => e.stopPropagation()}>
          <CartHeader>
            <h2>Votre panier</h2>
            <button className="close-btn" onClick={() => setIsCartOpen(false)}>
              ×
            </button>
          </CartHeader>

          <CartContent>
            {cartItems.length === 0 ? (
              <EmptyCart>
                <div className="empty-icon">🛒</div>
                <h3>Votre panier est vide</h3>
                <p>Découvrez nos iPhone et offres exclusives!</p>
                <button onClick={() => setIsCartOpen(false)}>
                  Voir les produits
                </button>
              </EmptyCart>
            ) : (
              cartItems.map((item, index) => (
                <CartItem key={`${item.id}-${item.storage}-${item.color}-${index}`}>
                  <div className="product-image">
                    {item.emoji}
                  </div>
                  <div className="product-details">
                    <h3>{item.name || item.product?.name}</h3>
                    <div className="config">
                      {item.storage} • {item.color}
                    </div>
                    {item.offer && (
                      <div className="offer-badge">
                        OFFRE FLASH -{Math.round((1 - item.price / item.originalPrice) * 100)}%
                      </div>
                    )}
                  </div>
                  <div className="product-actions">
                    <div className="price">
                      {(item.total_price || (item.price * item.quantity)).toLocaleString()} FCFA
                    </div>
                    <div className="quantity-controls">
                      <input
                        type="number"
                        min="1"
                        max="99"
                        value={item.quantity}
                        onChange={(e) => {
                          const newQuantity = parseInt(e.target.value) || 1;
                          if (newQuantity > 0 && newQuantity <= 99) {
                            updateQuantity(item.id, item.storage, item.color, newQuantity);
                          }
                        }}
                        className="quantity-input"
                      />
                    </div>
                    <button 
                      className="remove-btn"
                      onClick={() => removeFromCart(item.id, item.storage, item.color)}
                    >
                      Supprimer
                    </button>
                  </div>
                </CartItem>
              ))
            )}
          </CartContent>

          {cartItems.length > 0 && (
            <CartFooter>
              <div className="total-row">
                <span className="total-label">Total:</span>
                <span className="total-price">{getCartTotal().toLocaleString()} FCFA</span>
              </div>
              <div className="cart-actions">
                <button className="checkout-btn" onClick={handleCheckout}>
                  Finaliser la commande ({getCartCount()} article{getCartCount() > 1 ? 's' : ''})
                </button>
                <button className="continue-btn" onClick={() => setIsCartOpen(false)}>
                  Continuer mes achats
                </button>
              </div>
            </CartFooter>
          )}
        </CartSidebar>
      </CartOverlay>
    </>
  );
}

export default Cart;
